import React from 'react';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { Container } from '@cp/ds/src/components/container';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { Stack } from '@cp/ds/src/components/stack';
import { Button } from '@cp/ds/src/components/button';
import { ArrowRightIcon } from '@cp/ds/src/icons';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '@cp/ds/src/theme';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { CatalogItemListFilter } from '@common/model/catalog/item/list.filter';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useMediaQuery } from '@mui/material';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { Typography } from '@cp/ds/src/components/typography';
import { coreCareerApi } from '@cp/entities/career';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useRouter } from '@cp/shared/router';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { CatalogPlaces } from '@common/model/blocks';
import { ResumeFeaturesProvider } from '@cp/entities/resume';
import { useH1 } from 'src/app/shared/hooks/useH1';
import { Header } from 'src/widgets/header';
import { H1 } from 'src/app/shared/components/h1';
import { CatalogClientsOnlineTotalCounter } from 'src/widgets/CatalogClientsOnlineTotalCounter';
import {
  CatalogList,
  CitySelection,
  FAQ,
  FavoriteFeature,
  ResumeCardWidget,
  ResumeCardWidgetV2,
  VacancyCardWidget,
  VerifiedFeature,
  useCatalogBreadcrumbs,
} from 'src/widgets/catalog';
import { CatalogFeedbacks } from 'src/widgets/catalog/feedback/list';
import { CatalogBannerSearch } from 'src/entities/catalog/banner';
import { CatalogSpecializationTagCloud } from 'src/widgets/CatalogSpecializationTagCloud';
import { CatalogCareerList } from 'src/widgets/CatalogCareerList';
import { useAuth, useAuthInterceptor } from 'src/features/auth';
import { CatalogItemView } from 'src/features/catalogItem/view';
import { CatalogItemActions } from 'src/widgets/catalog/item/actions';
import { RoutedLink } from 'src/shared/ui/link';
import { CatalogViewProps } from 'src/app/routes/catalog/types';
import { CatalogMetaTags } from 'src/app/routes/catalog/components/meta';
import { BannerServiceFeature } from 'src/widgets/catalog/ui/bannerServiceFeature';
import { BannerHowItWorksWidget } from 'src/widgets/catalog/ui/bannerHowItWorks';
import { CatalogItemModalViewType } from 'src/entities/catalog/item/ui';
import { BannerBlogPosts } from 'src/widgets/catalog/ui/bannerBlogPosts';
import { RenderRouteBlock } from 'src/features/blocks';
import { useCatalogHeaderConfig } from '../../hooks/useCatalogHeaderConfig';
import {
  CATALOG_DESKTOP_COLUMN_SPACING,
  CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
  CATALOG_DESKTOP_CONTAINER_MAX_WIDTH,
  CATALOG_MOBILE_COLUMN_SPACING,
} from '../../const';
import { calcDesktopItemListStyles, calcMobileItemListStyleParams } from '../../utils/calcItemListStyles';
import { buildSubcatalogInversedListRequest, buildSubcatalogListRequest } from '../../lib';
import { View as FooterView } from '../../../../shared/components/layout/components/footer/view';
import { useFeedbacksParameters } from '../../hooks/useFeedbacksParameters';

const LIST_MAX_WIDTH = 820;
const VACANCY_LIST_CONTAINER_SX: SxProps = { maxWidth: LIST_MAX_WIDTH, mx: 'auto' };

export const SubcatalogPage: React.FC<CatalogViewProps> = ({ currentCity, pageProps }) => {
  const router = useRouter();
  const pathGenerator = usePathGenerator();
  const { careerId, catalogType } = pageProps;
  const isResumeCatalogType = catalogType === CatalogItemType.Resume;
  const filter: CatalogItemListFilter | undefined = undefined;
  const loggedIn = useIsLoggedIn();
  const login = useAuth();
  const h1RenderConfig = useH1();
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const interceptAuth = useAuthInterceptor();
  const { containerSx: resumeListContainerSx, cardWidthAutoUntilBreakpoint: resumeCardWidthAutoUntilBreakpoint } = React.useMemo(
    () => calcDesktopItemListStyles(theme, { widthModifierForMainBlock: 0 }),
    [theme],
  );
  const { columnSpacing } = React.useMemo(() => calcMobileItemListStyleParams(theme), [theme]);
  const isResumeCardWidthFull = useMediaQuery(theme.breakpoints.down(resumeCardWidthAutoUntilBreakpoint));
  const containerMaxWidth = isMobile ? undefined : CATALOG_DESKTOP_CONTAINER_MAX_WIDTH;
  const feedbacksListParameters = useFeedbacksParameters({ careerId, catalogType, careerDictionary, currentCity });

  const { listRequest, inverseListRequest } = React.useMemo<{
    listRequest: CatalogItemListRequest;
    inverseListRequest: CatalogItemListRequest;
  }>(
    () => ({
      listRequest: buildSubcatalogListRequest({ pageProps, cityId: currentCity.id }),
      inverseListRequest: buildSubcatalogInversedListRequest({ pageProps, cityId: currentCity.id }),
    }),
    [currentCity.id, pageProps],
  );

  const handleSearch = interceptAuth(() =>
    router.navigate(pathGenerator({ page: ClientWebPage.Catalog, pageProps: { careerId, catalogType }, queryParams: { filter } })),
  );
  const breadcrumbs = useCatalogBreadcrumbs();

  const renderSection = ({
    title,
    body,
    containerProps,
  }: {
    body: React.ReactNode;
    title?: React.ReactNode;
    containerProps?: BoxProps;
  }): React.ReactNode => {
    return (
      <Box {...containerProps} sx={mergeSx(containerProps?.sx, { mt: { xs: 5, [MOBILE_BREAKPOINT_THRESHOLD]: 10 } })}>
        {title && (
          <Typography component="h2" sx={{ mb: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 }, textAlign: 'center' }} variant="h2">
            {title}
          </Typography>
        )}
        {body}
      </Box>
    );
  };

  return (
    <>
      <CatalogMetaTags listRequest={listRequest} pageProps={pageProps} />
      <Box component="main" sx={{ backgroundColor: 'background.paper', pb: 15 }}>
        <Header
          {...useCatalogHeaderConfig()}
          containerProps={{ maxWidth: containerMaxWidth }}
          sx={{
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
            zIndex: 5,
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
          }}
        />

        <Container maxWidth={containerMaxWidth}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, my: 1.5 }}>
            <Breadcrumbs LinkComponent={RoutedLink} items={breadcrumbs} />
          </Box>
          <H1
            config={h1RenderConfig}
            itemProps={{ bold: { variant: 'h1' } }}
            rootProps={{
              sx: { mt: 4, mb: 0, textAlign: 'center' },
              variant: 'h1',
            }}
          />

          <Stack
            direction={{ xs: 'column', [MOBILE_BREAKPOINT_THRESHOLD]: 'row' }}
            spacing={{ xs: 1, [MOBILE_BREAKPOINT_THRESHOLD]: 4 }}
            sx={{ py: { xs: 1.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 }, justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              color="pink"
              iconRight={<ArrowRightIcon />}
              onClick={handleSearch}
              size={isMobile ? 'small' : 'normal'}
              variant="primary"
            >
              Начать поиск
            </Button>
            <CatalogClientsOnlineTotalCounter careerId={careerId} filter={filter} itemType={catalogType} />
          </Stack>

          {renderSection({
            body: (
              <Box
                sx={mergeSx(isResumeCatalogType ? resumeListContainerSx : VACANCY_LIST_CONTAINER_SX, {
                  mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 },
                })}
              >
                <ResumeFeaturesProvider
                  value={{
                    sidebar: {
                      right: [FavoriteFeature, VerifiedFeature],
                    },
                  }}
                >
                  <CatalogList
                    CatalogItem={isResumeCatalogType ? ResumeCardWidgetV2 : VacancyCardWidget}
                    itemSpacing={CATALOG_MOBILE_COLUMN_SPACING}
                    itemWidth={
                      !isResumeCatalogType
                        ? '100%'
                        : isResumeCardWidthFull
                          ? `calc((100% - ${columnSpacing}px)/2)`
                          : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED
                    }
                    request={listRequest}
                  />
                </ResumeFeaturesProvider>
              </Box>
            ),
          })}

          {isResumeCatalogType && (
            <Box
              sx={{
                mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 },
              }}
            >
              <Typography component="h2" sx={{ mb: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 }, textAlign: 'center' }} variant="h2">
                {/* ToDo. Fix */}
                {}
                {`300 ${careerDictionary?.[careerId].translation.namePluralGenitive} в ${currentCity.translation.namePrepositional}`}
              </Typography>
              <BannerServiceFeature />
            </Box>
          )}

          <RenderRouteBlock blockName={CatalogPlaces.infoContent} sx={{ mt: { xs: 1.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 } }} />

          {isResumeCatalogType &&
            renderSection({
              title: `Отзывы о наших ${careerDictionary?.[careerId].translation.namePluralPrepositional} в ${currentCity.translation.namePrepositional}`,
              body: <CatalogFeedbacks {...feedbacksListParameters} />,
            })}

          {renderSection({
            title: isResumeCatalogType ? 'Прямо сейчас ищут' : 'Наши специалисты',
            body: (
              <Box
                sx={mergeSx(!isResumeCatalogType ? resumeListContainerSx : VACANCY_LIST_CONTAINER_SX, {
                  mt: { xs: 2.5, [MOBILE_BREAKPOINT_THRESHOLD]: 5 },
                })}
              >
                {/* Inverse catalog list. Inverse widgets too */}
                <CatalogList
                  CatalogItem={isResumeCatalogType ? VacancyCardWidget : ResumeCardWidget}
                  itemSpacing={CATALOG_DESKTOP_COLUMN_SPACING}
                  itemWidth={isResumeCardWidthFull || isResumeCatalogType ? '100%' : CATALOG_DESKTOP_COLUMN_WIDTH_FIXED}
                  request={inverseListRequest}
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            ),
          })}

          {isResumeCatalogType && <BannerHowItWorksWidget />}

          {!loggedIn && (
            <CatalogBannerSearch
              careerDict={careerDictionary}
              careerId={careerId}
              itemType={catalogType}
              onClick={() => login()}
              sx={{ mt: { xs: 5, [MOBILE_BREAKPOINT_THRESHOLD]: 10 }, borderRadius: '24px', overflow: 'hidden' }}
            />
          )}

          {isResumeCatalogType &&
            renderSection({
              title: 'Часто задаваемые вопросы',
              /* eslint-disable-next-line react/jsx-pascal-case */
              body: <FAQ listRequest={listRequest} />,
              containerProps: { sx: { maxWidth: LIST_MAX_WIDTH, mx: 'auto' } },
            })}

          {renderSection({
            title: 'Похожие страницы',
            body: (
              <CatalogSpecializationTagCloud
                careerId={careerId}
                itemType={catalogType}
                maxVisibleItemsCountByDefault={Number.MAX_SAFE_INTEGER}
                sx={{ border: 'none', p: 0, backgroundColor: 'transparent', justifyContent: 'center' }}
                title={false}
              />
            ),
          })}

          {renderSection({
            title: 'Смотрите также',
            body: <CatalogCareerList itemType={catalogType} />,
          })}

          {renderSection({
            title: 'Вам может быть полезным',
            body: <BannerBlogPosts />,
          })}
        </Container>
      </Box>

      {/* Preview catalog item */}
      <CatalogItemView
        buttons={catalogType === CatalogItemType.Resume && isMobile ? undefined : <CatalogItemActions />}
        type={CatalogItemModalViewType.Single}
      />

      {isMobile && <FooterView />}

      <CitySelection />
    </>
  );
};
